import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, Save, User, Lock, Shield, Key, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLIENT_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'business', label: 'Business' },
  { value: 'law_firm', label: 'Law Firm' },
  { value: 'bail_bond', label: 'Bail Bond Company' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'government', label: 'Government' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
  { value: 'archived', label: 'Archived' },
];

const ClientFormCtx = React.createContext({ formData: {}, setFormData: () => {} });

const FormSection = ({ title, icon: Icon, children }) => (
  <div className="mb-6 bg-slate-800/50 rounded-lg p-4 border border-slate-700">
    <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-600">
      {Icon && <Icon className="w-5 h-5 text-blue-400" />}
      <h4 className="text-sm font-semibold text-slate-200">{title}</h4>
    </div>
    {children}
  </div>
);

const FormRow = ({ children, cols = 2 }) => (
  <div className={`grid gap-4 mb-3 ${cols === 1 ? 'grid-cols-1' : cols === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3'}`}>
    {children}
  </div>
);

const FormField = ({ label, name, type = 'text', required, options, placeholder }) => {
  const { formData, setFormData } = React.useContext(ClientFormCtx);
  const onChange = (e) => setFormData((prev) => ({ ...prev, [name]: e.target.value }));
  return (
    <div>
      <label className="block text-xs font-medium text-slate-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {type === 'select' ? (
        <select value={formData[name] ?? ''} onChange={onChange} className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500">
          {options?.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={formData[name] ?? ''} onChange={onChange} className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500" placeholder={placeholder} rows={3} />
      ) : (
        <Input type={type} value={formData[name] ?? ''} onChange={onChange} className="w-full bg-slate-700 border-slate-600 text-white" placeholder={placeholder} required={required} autoComplete="off" />
      )}
    </div>
  );
};

const PermissionToggle = ({ label, name }) => {
  const { formData, setFormData } = React.useContext(ClientFormCtx);
  return (
    <div className="flex items-center justify-between py-2 px-3 bg-slate-700/50 rounded">
      <span className="text-sm text-slate-300">{label}</span>
      <button
        type="button"
        onClick={() => setFormData((prev) => ({ ...prev, [name]: !prev[name] }))}
        disabled={!formData.portal_enabled}
        className={`w-12 h-6 rounded-full transition-colors ${formData[name] && formData.portal_enabled ? 'bg-blue-500' : 'bg-slate-600'} ${!formData.portal_enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span className={`block w-4 h-4 bg-white rounded-full transform transition-transform ${formData[name] ? 'translate-x-7' : 'translate-x-1'}`} />
      </button>
    </div>
  );
};

const emptyForm = {
  first_name: '', last_name: '', business_name: '', client_type: 'individual',
  primary_email: '', secondary_email: '', primary_phone: '', secondary_phone: '',
  address_line1: '', address_line2: '', city: '', state: '', zip_code: '',
  notes: '', internal_notes: '', status: 'active',
  portal_enabled: false, portal_username: '', portal_password: '', portal_password_confirm: '',
  require_password_change: true, send_welcome_email: false, send_password_setup_link: false, portal_active: true,
  can_view_assigned_cases: true, can_view_reports: true, can_view_documents: true,
  can_upload_files: false, can_send_messages: true, can_view_status_timeline: true,
  can_view_attachments: true, can_receive_case_updates: true,
};

/**
 * Phase B — Routed Client Add/Edit page (was a `fixed inset-0` modal).
 * Routes: /clients/new  and  /clients/:clientId/edit
 */
const ClientFormPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEditing = Boolean(clientId);

  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadClient = useCallback(async () => {
    if (!isEditing) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}`, { headers, credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const client = await res.json();
      const perms = client.permissions || {};
      setFormData({
        ...emptyForm,
        first_name: client.first_name || (client.name || '').split(' ')[0] || '',
        last_name: client.last_name || (client.name || '').split(' ').slice(1).join(' ') || '',
        business_name: client.business_name || client.company_name || '',
        client_type: client.client_type || 'individual',
        primary_email: client.primary_email || client.email || '',
        secondary_email: client.secondary_email || '',
        primary_phone: client.primary_phone || client.phone || '',
        secondary_phone: client.secondary_phone || '',
        address_line1: client.address_line1 || client.address || '',
        address_line2: client.address_line2 || '',
        city: client.city || '', state: client.state || '', zip_code: client.zip_code || '',
        notes: client.notes || '', internal_notes: client.internal_notes || '',
        status: client.status || 'active',
        portal_enabled: client.portal_enabled || false,
        portal_username: client.portal_username || '',
        require_password_change: client.require_password_change ?? true,
        portal_active: client.portal_active ?? true,
        can_view_assigned_cases: perms.can_view_assigned_cases ?? true,
        can_view_reports: perms.can_view_reports ?? true,
        can_view_documents: perms.can_view_documents ?? true,
        can_upload_files: perms.can_upload_files ?? false,
        can_send_messages: perms.can_send_messages ?? true,
        can_view_status_timeline: perms.can_view_status_timeline ?? true,
        can_view_attachments: perms.can_view_attachments ?? true,
        can_receive_case_updates: perms.can_receive_case_updates ?? true,
      });
    } catch (e) {
      toast.error('Failed to load client');
    } finally {
      setLoading(false);
    }
  }, [clientId, isEditing, token]);

  useEffect(() => { loadClient(); }, [loadClient]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.primary_email) {
      toast.error('First name and email are required');
      return;
    }
    if (formData.portal_enabled) {
      if (!formData.portal_username) {
        toast.error('Portal username is required when portal access is enabled');
        return;
      }
      if (formData.portal_password && formData.portal_password !== formData.portal_password_confirm) {
        toast.error('Passwords do not match');
        return;
      }
      if (formData.portal_password && formData.portal_password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
    }
    setBusy(true);
    try {
      const submitData = { ...formData };
      delete submitData.portal_password_confirm;
      const url = isEditing ? `${API_URL}/api/clients/${clientId}` : `${API_URL}/api/clients`;
      const method = isEditing ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers, credentials: 'include', body: JSON.stringify(submitData) });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Operation failed');
        return;
      }
      toast.success(isEditing ? 'Client updated' : 'Client created');
      if (!isEditing && formData.portal_enabled) {
        const pw = data.temp_password || formData.portal_password;
        if (pw) {
          setCredentials({ username: formData.portal_username, password: pw });
          return; // stay on page to show creds
        }
      }
      navigate('/clients');
    } catch (err) {
      toast.error('Failed to save client');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-300 p-6">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading client…
      </div>
    );
  }

  if (credentials) {
    return (
      <div className="max-w-md mx-auto space-y-4" data-testid="client-credentials-panel">
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-slate-600 !text-slate-200" onClick={() => navigate('/clients')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Clients
          </Button>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Key className="w-5 h-5 text-yellow-400" /> Portal Login Credentials
          </h1>
        </div>
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 space-y-4">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3 text-sm text-yellow-200">
            <strong>Important:</strong> Save these credentials now. The password cannot be retrieved later.
          </div>
          <div className="p-3 bg-slate-700/50 rounded">
            <p className="text-xs text-slate-400 mb-1">Username</p>
            <p className="text-white font-mono text-base break-all">{credentials.username}</p>
          </div>
          <div className="p-3 bg-slate-700/50 rounded">
            <p className="text-xs text-slate-400 mb-1">Password</p>
            <p className="text-white font-mono text-base break-all">{credentials.password}</p>
          </div>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(`Username: ${credentials.username}\nPassword: ${credentials.password}\nURL: /client/login`);
              toast.success('Credentials copied to clipboard');
            }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            Copy Credentials to Clipboard
          </Button>
          <Button variant="outline" className="w-full border-slate-600 !text-slate-200" onClick={() => navigate('/clients')}>
            Done — Return to Client List
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ClientFormCtx.Provider value={{ formData, setFormData }}>
      <div className="max-w-4xl mx-auto space-y-4" data-testid="client-form-page">
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-slate-600 !text-slate-200 hover:bg-slate-800" onClick={() => navigate('/clients')} data-testid="client-form-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Clients
          </Button>
          <h1 className="text-2xl font-bold text-white">
            {isEditing ? 'Edit Client' : 'Create New Client'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <FormSection title="CLIENT PROFILE" icon={User}>
            <FormRow>
              <FormField label="First Name" name="first_name" required placeholder="John" />
              <FormField label="Last Name" name="last_name" placeholder="Smith" />
            </FormRow>
            <FormRow>
              <FormField label="Business Name" name="business_name" placeholder="Company Inc." />
              <FormField label="Client Type" name="client_type" type="select" options={CLIENT_TYPES} />
            </FormRow>
            <FormRow>
              <FormField label="Primary Email" name="primary_email" type="email" required placeholder="john@example.com" />
              <FormField label="Secondary Email" name="secondary_email" type="email" placeholder="john.backup@example.com" />
            </FormRow>
            <FormRow>
              <FormField label="Primary Phone" name="primary_phone" placeholder="(555) 123-4567" />
              <FormField label="Secondary Phone" name="secondary_phone" placeholder="(555) 987-6543" />
            </FormRow>
            <FormRow>
              <FormField label="Address Line 1" name="address_line1" placeholder="123 Main Street" />
              <FormField label="Address Line 2" name="address_line2" placeholder="Suite 100" />
            </FormRow>
            <FormRow cols={3}>
              <FormField label="City" name="city" placeholder="Dallas" />
              <FormField label="State" name="state" placeholder="TX" />
              <FormField label="ZIP Code" name="zip_code" placeholder="75001" />
            </FormRow>
            <FormRow>
              <FormField label="Status" name="status" type="select" options={STATUS_OPTIONS} />
            </FormRow>
            <FormRow cols={1}>
              <FormField label="Notes" name="notes" type="textarea" placeholder="Client notes..." />
            </FormRow>
            <FormRow cols={1}>
              <FormField label="Internal Notes (not visible to client)" name="internal_notes" type="textarea" placeholder="Internal notes only..." />
            </FormRow>
          </FormSection>

          <FormSection title="PORTAL LOGIN SETUP" icon={Lock}>
            <div className="flex items-center justify-between mb-4 p-3 bg-slate-700/50 rounded">
              <div>
                <p className="text-sm font-medium text-white">Enable Client Portal Access</p>
                <p className="text-xs text-slate-400">Allow this client to log in to the portal</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, portal_enabled: !prev.portal_enabled }))}
                className={`w-14 h-7 rounded-full transition-colors ${formData.portal_enabled ? 'bg-blue-500' : 'bg-slate-600'}`}
              >
                <span className={`block w-5 h-5 bg-white rounded-full transform transition-transform ${formData.portal_enabled ? 'translate-x-8' : 'translate-x-1'}`} />
              </button>
            </div>
            {formData.portal_enabled && (
              <>
                <FormRow>
                  <FormField label="Portal Username" name="portal_username" placeholder="Leave blank to use email" />
                </FormRow>
                <FormRow>
                  <FormField label="Temporary Password" name="portal_password" type="password" placeholder="Leave blank to auto-generate" />
                  <FormField label="Confirm Password" name="portal_password_confirm" type="password" placeholder="Confirm password" />
                </FormRow>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <PermissionToggle label="Require Password Change on First Login" name="require_password_change" />
                  <PermissionToggle label="Portal Starts Active" name="portal_active" />
                </div>
              </>
            )}
          </FormSection>

          {formData.portal_enabled && (
            <FormSection title="CLIENT PORTAL ACCESS SETTINGS" icon={Shield}>
              <p className="text-xs text-slate-400 mb-4">Configure what the client can see and do in their portal</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <PermissionToggle label="View Assigned Cases" name="can_view_assigned_cases" />
                <PermissionToggle label="View Reports" name="can_view_reports" />
                <PermissionToggle label="View Documents" name="can_view_documents" />
                <PermissionToggle label="Upload Files" name="can_upload_files" />
                <PermissionToggle label="Send Messages" name="can_send_messages" />
                <PermissionToggle label="View Status Timeline" name="can_view_status_timeline" />
                <PermissionToggle label="View Attachments" name="can_view_attachments" />
                <PermissionToggle label="Receive Case Updates" name="can_receive_case_updates" />
              </div>
            </FormSection>
          )}

          <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-6 pt-4 border-t border-slate-700">
            <Button type="button" variant="outline" onClick={() => navigate('/clients')} className="w-full sm:w-auto border-slate-600 !text-slate-200">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto disabled:opacity-60" data-testid="save-client-btn">
              {busy ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />{isEditing ? 'Saving…' : 'Creating…'}</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />{isEditing ? 'Update Client' : 'Create Client'}</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </ClientFormCtx.Provider>
  );
};

export default ClientFormPage;
